/**
 * SignIn — Ekselens-hosted interstitial for the federated ERIO SSO login.
 *
 * Rather than linking the header "Sign in" button straight at ERIO's OIDC
 * endpoint (which returns a raw `{"detail":"OIDC SSO not configured"}` JSON
 * page until ERIO ships its side), we route here first. This page asks the
 * backend whether ERIO's OIDC login is live:
 *   · ready  → forwards to the ERIO OIDC login immediately.
 *   · not    → shows a branded "sign-in is being set up" message + retry,
 *              so a real visitor never sees an error page.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { ssoLoginUrl, useSsoSession } from "../lib/ssoSession";

// Preview / non-production runs with REACT_APP_AUTH_PROVIDER='emergent' have no
// Auth0 callback URL registered for their (ever-changing) preview domain, so the
// public sign-in uses Emergent Google Auth there. Production (auth0/both) keeps
// the first-party Auth0 OIDC SSO.
const EMERGENT_MODE = (process.env.REACT_APP_AUTH_PROVIDER || "emergent").toLowerCase() === "emergent";

// Loop-breaker: we stamp the moment we bounce out to the identity provider. If
// we land back here still unauthenticated within this window, the round-trip
// failed to establish a session (usually an Auth0 callback-URL / cookie-domain
// mismatch) — so we STOP auto-redirecting and show a manual retry instead of
// looping forever.
const ATTEMPT_KEY = "ek-sso-attempt-ts";
const LOOP_WINDOW_MS = 45000;

export default function SignIn() {
  const { signedIn, name } = useSsoSession();
  const reason = new URLSearchParams(window.location.search).get("reason");
  const timedOut = reason === "idle" || reason === "session_cap";
  const [stuck, setStuck] = useState(false);
  const didRun = useRef(false); // guard against React.StrictMode double-invoke

  const readAttempt = () => {
    try { return Number(window.sessionStorage.getItem(ATTEMPT_KEY)) || 0; } catch { return 0; }
  };
  const stampAttempt = () => {
    try { window.sessionStorage.setItem(ATTEMPT_KEY, String(Date.now())); } catch {/*noop*/}
  };
  const clearAttempt = () => {
    try { window.sessionStorage.removeItem(ATTEMPT_KEY); } catch {/*noop*/}
  };

  const redirectToIdp = () => {
    stampAttempt();
    if (EMERGENT_MODE) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/admin";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    window.location.href = ssoLoginUrl();
  };

  const goToLogin = () => { clearAttempt(); setStuck(false); redirectToIdp(); };

  useEffect(() => {
    if (signedIn) { clearAttempt(); return; }
    // Paused when arriving from an inactivity / session-cap logout so the user
    // sees why they were signed out before being forwarded to sign-in again.
    if (timedOut) return;
    // Run the redirect decision exactly once (StrictMode invokes effects twice
    // in dev; a second pass must not re-read cleared state and re-redirect).
    if (didRun.current) return;
    didRun.current = true;
    // If we already bounced to the IdP very recently and are back here still
    // unauthenticated, the sign-in round-trip failed — break the loop. We keep
    // the attempt stamp in place (only an explicit retry or a real session
    // clears it) so the double-invoke can't slip past the guard.
    const last = readAttempt();
    if (last && Date.now() - last < LOOP_WINDOW_MS) {
      setStuck(true);
      return;
    }
    redirectToIdp();
  }, [signedIn, timedOut]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="min-h-[70vh] bg-black text-white flex items-center" data-testid="signin-page">
      <div className="ek-container py-24 w-full max-w-xl">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#A179FA] mb-4">EKSELENS360 · Single sign-on</div>

        {signedIn ? (
          <div data-testid="signin-already">
            <h1 className="text-3xl font-medium mb-3">You&rsquo;re already signed in{name ? `, ${name}` : ""}.</h1>
            <p className="text-white/70 text-[15px] leading-relaxed mb-8">
              Your shared session is active across Ekselens and ERIO.
            </p>
            <Link to="/" className="ek-btn ek-btn-primary inline-flex items-center gap-2" data-testid="signin-home-link">
              <ArrowLeft size={15} /> Back to home
            </Link>
          </div>
        ) : stuck ? (
          <div data-testid="signin-stuck">
            <h1 className="text-3xl font-medium mb-3 flex items-center gap-3">
              <AlertTriangle size={24} className="text-[#A179FA]" /> We couldn&rsquo;t complete sign-in.
            </h1>
            <p className="text-white/70 text-[15px] leading-relaxed mb-8">
              You were returned from sign-in without an active session. This usually clears on a
              second try. If it keeps happening, make sure you&rsquo;re signing in on the main site
              address and that cookies aren&rsquo;t blocked.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={goToLogin} className="ek-btn ek-btn-primary inline-flex items-center gap-2" data-testid="signin-stuck-retry">
                Try signing in again
              </button>
              <Link to="/" className="ek-btn ek-btn-outline-light inline-flex items-center gap-2" data-testid="signin-stuck-home">
                <ArrowLeft size={15} /> Back to home
              </Link>
            </div>
          </div>
        ) : timedOut ? (
          <div data-testid="signin-timed-out">
            <h1 className="text-3xl font-medium mb-3">You were signed out.</h1>
            <p className="text-white/70 text-[15px] leading-relaxed mb-8">
              {reason === "session_cap"
                ? "Your session reached its maximum length. Please sign in again to continue."
                : "For security, you were signed out due to inactivity. Please sign in again to continue."}
            </p>
            <button onClick={goToLogin} className="ek-btn ek-btn-primary inline-flex items-center gap-2" data-testid="signin-retry">
              Sign in again
            </button>
          </div>
        ) : (
          <div data-testid="signin-checking">
            <h1 className="text-3xl font-medium mb-3 flex items-center gap-3">
              <Loader2 size={26} className="animate-spin text-[#A179FA]" /> Taking you to sign-in&hellip;
            </h1>
            <p className="text-white/70 text-[15px] leading-relaxed">
              Connecting to the shared Ekselens &amp; ERIO single sign-on.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
